import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Receipt, CheckCircle, XCircle, Hourglass, ClockCounterClockwise } from "@phosphor-icons/react";

/**
 * DevisHistorySection — timeline of the Axonaut quotes attached to this
 * chantier (initial + complementary). Rendered on ChantierDetail.
 *
 * Backend: GET /api/chantiers/{id}/devis-history
 *   - Techs get `total_ht: null` (price-hidden)
 *   - Empty response means "no quote linked" → hide the section entirely
 */

const STATE_META = {
  accepte:      { label: "Accepté",    color: "#10B981", icon: CheckCircle },
  refuse:       { label: "Refusé",     color: "#EF4444", icon: XCircle },
  chiffrage:    { label: "En cours",   color: "#F59E0B", icon: Hourglass },
};

const TYPE_META = {
  initial:       { label: "Initial",       color: "#005CFF" },
  complementaire:{ label: "Complémentaire", color: "#8B5CF6" },
};

export default function DevisHistorySection({ chantierId }) {
  const [rows, setRows] = useState(null); // null = loading, [] = empty (hidden)

  useEffect(() => {
    if (!chantierId) return;
    let alive = true;
    api
      .get(`/chantiers/${chantierId}/devis-history`)
      .then((r) => { if (alive) setRows(r.data || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [chantierId]);

  // Hide entirely if no quotes linked — avoids empty-state noise on legacy chantiers
  if (!rows || rows.length === 0) return null;

  const fmt = (n) =>
    (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

  return (
    <div
      className="rounded-2xl bg-white/[0.03] border border-white/10 p-4"
      data-testid="devis-history-section"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#005CFF]/20 flex items-center justify-center">
          <ClockCounterClockwise size={16} weight="duotone" className="text-[#7CB2FF]" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold text-sm">Historique des devis</div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">
            {rows.length} devis Axonaut lié{rows.length > 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div className="space-y-2" data-testid="devis-history-list">
        {rows.map((d) => {
          const st = STATE_META[d.state] || STATE_META.chiffrage;
          const tp = TYPE_META[d.type] || TYPE_META.initial;
          const Icon = st.icon;
          return (
            <div
              key={d.id}
              className="rounded-xl bg-black/30 border border-white/8 px-3 py-2.5 flex items-center gap-3"
              data-testid={`devis-history-row-${d.id}`}
            >
              <div className="w-1 h-10 rounded-full shrink-0" style={{ background: tp.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">
                    <Receipt size={13} weight="duotone" className="inline mr-1 text-white/60" />
                    {d.number || "—"}
                  </span>
                  <span
                    className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-semibold"
                    style={{ background: `${tp.color}22`, color: tp.color }}
                  >
                    {tp.label}
                  </span>
                </div>
                <div className="text-[10px] text-white/40 mt-0.5">
                  {d.date ? new Date(d.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "Date inconnue"}
                </div>
              </div>
              <div className="text-right shrink-0">
                {d.total_ht !== null && d.total_ht !== undefined && (
                  <div className="font-display font-bold text-sm tabular-nums">
                    {fmt(d.total_ht)}
                    <span className="text-[9px] text-white/40 ml-1">HT</span>
                  </div>
                )}
                <div className="flex items-center gap-1 justify-end mt-0.5" style={{ color: st.color }}>
                  <Icon size={11} weight="fill" />
                  <span className="text-[10px] uppercase tracking-widest font-semibold">{st.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
